import { useCallback, useEffect, useState } from 'react';
import { api, type ApiError, type DocVersion, type SkillSummary } from '../api';
import { useAuth } from '../auth';
import { Button } from '../ui/Button';
import { MarkdownEditor, type MdSelection } from '../ui/MarkdownEditor';
import { AiChatPanel, type ChatTurn } from '../ui/AiChatPanel';
import { VersionHistory } from '../ui/VersionHistory';

/**
 * Biblioteca GLOBAL de skills (página) — padrão agentskills.io.
 *
 * Quando o hermes está conectado, espelha as skills reais dele (editar = atualizar o
 * hermes). Reusa os MESMOS componentes da base de docs: `MarkdownEditor` (toggle
 * Visualização/Código + seleção) e `AiChatPanel` (chat de IA com trecho selecionado).
 *
 * Acesso: leitura pra qualquer usuário; escrita pra admin (P1 — o backend gateia).
 */
export function SkillLibrary() {
  const { me } = useAuth();
  const canEdit = me?.globalRole === 'admin';

  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [loadingSkills, setLoadingSkills] = useState(true);
  const [models, setModels] = useState<{ claude: string[]; hermes: string[] }>({ claude: [], hermes: [] });
  const [selected, setSelected] = useState<SkillSummary | null>(null);
  const [content, setContent] = useState('');
  const [savedContent, setSavedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Trecho selecionado (qualquer modo) → foco do chat de IA.
  const [selection, setSelection] = useState<MdSelection | null>(null);

  // Coluna da direita: chat de IA ou histórico de versões.
  const [rightTab, setRightTab] = useState<'chat' | 'history'>('chat');
  const [history, setHistory] = useState<DocVersion[]>([]);
  const [previewSha, setPreviewSha] = useState<string | null>(null);

  // Conversa do assistente de IA (por skill aberta).
  const [aiMsgs, setAiMsgs] = useState<ChatTurn[]>([]);
  const [aiBusy, setAiBusy] = useState(false);

  const dirty = content !== savedContent;
  const isPreviewing = previewSha !== null;

  const loadSkills = useCallback(async () => {
    try {
      const { skills } = await api.getSkills();
      setSkills(skills);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoadingSkills(false);
    }
  }, []);

  useEffect(() => {
    void loadSkills();
    api.getSkillModels().then(setModels).catch(() => undefined);
  }, [loadSkills]);

  const open = useCallback(async (skill: SkillSummary) => {
    setLoading(true);
    setError(null);
    setPreviewSha(null);
    setSelection(null);
    setAiMsgs([]);
    try {
      const { content } = await api.getSkill(skill.path);
      setSelected(skill);
      setContent(content);
      setSavedContent(content);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshHistory = useCallback(async (path: string) => {
    try {
      const { versions } = await api.skillHistory(path);
      setHistory(versions);
    } catch (e) {
      setError(errMsg(e));
    }
  }, []);

  // Salva o conteúdo atual (botão) ou um valor vindo do editor de Visualização.
  const persist = useCallback(
    async (v: string) => {
      if (!selected || isPreviewing) return;
      setSaving(true);
      setError(null);
      try {
        await api.saveSkill(selected.path, v);
        setContent(v);
        setSavedContent(v);
        await loadSkills();
        if (rightTab === 'history') await refreshHistory(selected.path);
      } catch (e) {
        setError(errMsg(e));
      } finally {
        setSaving(false);
      }
    },
    [selected, isPreviewing, loadSkills, rightTab, refreshHistory],
  );

  const preview = useCallback(async (sha: string) => {
    if (!selected) return;
    setError(null);
    try {
      const { content } = await api.skillAt(selected.path, sha);
      setContent(content);
      setPreviewSha(sha);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [selected]);

  const exitPreview = useCallback(() => {
    setContent(savedContent);
    setPreviewSha(null);
  }, [savedContent]);

  const revert = useCallback(async (v: DocVersion) => {
    if (!selected) return;
    if (!window.confirm(`Reverter "${selected.name}" para a versão de ${v.author} (${v.sha.slice(0, 7)})? Vira um novo commit.`)) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.skillRevert(selected.path, v.sha);
      setContent(res.content);
      setSavedContent(res.content);
      setPreviewSha(null);
      await refreshHistory(selected.path);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }, [selected, refreshHistory]);

  const openHistoryTab = useCallback(() => {
    setRightTab('history');
    if (selected) void refreshHistory(selected.path);
  }, [selected, refreshHistory]);

  const createSkill = useCallback(async () => {
    const raw = window.prompt('Nome da nova skill (ex: 09-content):')?.trim();
    if (!raw) return;
    const name = slugifySkill(raw);
    if (!name) {
      setError('Nome inválido (use letras minúsculas, números e hífens).');
      return;
    }
    const path = `${name}/SKILL.md`;
    setSaving(true);
    setError(null);
    try {
      await api.saveSkill(path, skillTemplate(name));
      await loadSkills();
      await open({ name, description: '', dir: name, path, origin: 'mine' });
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  }, [loadSkills, open]);

  // Chat de IA: reescreve a skill (ou o trecho selecionado) e aplica no editor.
  const handleAiSend = useCallback(
    async (text: string) => {
      if (!selected) return;
      setAiMsgs((m) => [...m, { role: 'user', content: text }]);
      setAiBusy(true);
      try {
        const payload: { instruction: string; content: string; selection?: { text: string; from?: number; to?: number } } = {
          instruction: text,
          content,
        };
        if (selection) {
          payload.selection =
            selection.from != null && selection.to != null
              ? { text: selection.text, from: selection.from, to: selection.to }
              : { text: selection.text };
        }
        const res = await api.aiEditSkill(payload);
        setContent(res.newContent); // fica "dirty" → usuário revisa e salva
        setSelection(null);
        setAiMsgs((m) => [
          ...m,
          { role: 'assistant', content: res.replacedSelection ? 'Reescrevi o trecho selecionado — revise e salve.' : 'Atualizei a skill — revise e salve.' },
        ]);
      } catch (e) {
        setAiMsgs((m) => [...m, { role: 'assistant', content: `Erro: ${errMsg(e)}` }]);
      } finally {
        setAiBusy(false);
      }
    },
    [selected, content, selection],
  );

  return (
    <div className="page skill-lib-page">
      <div className="page-header">
        <div className="page-header-title">
          <h1>Biblioteca de Skills</h1>
          <div className="page-header-subtitle">
            {loadingSkills
              ? 'Carregando skills do Hermes…'
              : `${skills.length} skills (padrão agentskills.io) — cada projeto importa e melhora à sua maneira.`}
            {!canEdit && ' (somente leitura)'}
          </div>
        </div>
        {canEdit && (
          <Button variant="primary" onClick={createSkill} disabled={saving || loadingSkills}>
            + Nova skill
          </Button>
        )}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="skill-lib">
        <SkillList skills={skills} selected={selected?.path ?? null} onOpen={open} loading={loadingSkills} />

        <section className="skill-lib-main">
          {!selected ? (
            <div className="skill-lib-empty">Selecione uma skill à esquerda{canEdit ? ' ou crie uma nova' : ''}.</div>
          ) : (
            <>
              <div className="skill-lib-bar">
                <code className="skill-lib-path">{selected.path}</code>
                <div className="skill-lib-bar-right">
                  {isPreviewing && (
                    <span className="skill-lib-preview-tag">
                      vendo versão {previewSha?.slice(0, 7)}
                      <Button size="sm" variant="ghost" onClick={exitPreview}>voltar ao atual</Button>
                    </span>
                  )}
                  {canEdit && !isPreviewing && (
                    <label className="skill-lib-model" title="Modelo que esta skill usa (vazio = padrão do agente). Aceita qualquer slug.">
                      modelo:
                      <input
                        list="skill-models"
                        className="skill-lib-model-input"
                        value={getFrontmatterField(content, 'model') ?? ''}
                        placeholder="(padrão do agente)"
                        onChange={(e) => setContent(setFrontmatterField(content, 'model', e.target.value))}
                      />
                      <datalist id="skill-models">
                        {models.hermes.map((m) => (
                          <option key={`h-${m}`} value={m} />
                        ))}
                        {models.claude.map((m) => (
                          <option key={`c-${m}`} value={m} />
                        ))}
                      </datalist>
                    </label>
                  )}
                </div>
              </div>
              {loading ? (
                <div className="skill-lib-empty">Carregando…</div>
              ) : (
                <MarkdownEditor
                  value={content}
                  onChange={setContent}
                  onSaveBlock={persist}
                  onSelect={(s) => setSelection(s.text.trim() ? s : null)}
                  readOnly={!canEdit || isPreviewing}
                  rightSlot={
                    canEdit ? (
                      <Button size="sm" variant="primary" onClick={() => persist(content)} disabled={saving || !dirty || isPreviewing}>
                        {saving ? 'salvando…' : dirty ? 'Salvar' : 'Salvo'}
                      </Button>
                    ) : null
                  }
                />
              )}

              {/* Chat de IA / Histórico — ABAIXO do texto */}
              <div className="skill-lib-below">
                <div className="skill-lib-tabs">
                  <button type="button" className={`seg-btn ${rightTab === 'chat' ? 'is-active' : ''}`} onClick={() => setRightTab('chat')}>
                    Chat IA
                  </button>
                  <button type="button" className={`seg-btn ${rightTab === 'history' ? 'is-active' : ''}`} onClick={openHistoryTab}>
                    Histórico
                  </button>
                </div>
                {rightTab === 'chat' ? (
                  <AiChatPanel
                    messages={aiMsgs}
                    busy={aiBusy}
                    disabled={!canEdit || isPreviewing}
                    selection={selection?.text ?? null}
                    onClearSelection={() => setSelection(null)}
                    onSend={handleAiSend}
                    emptyHint='Selecione um trecho (em qualquer modo) e pergunte/peça alteração — ou escreva direto aqui.'
                  />
                ) : (
                  <VersionHistory
                    versions={history}
                    activeSha={previewSha}
                    busy={saving}
                    canRevert={canEdit}
                    onPreview={preview}
                    onRevert={revert}
                  />
                )}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

/** Lista lateral das skills (name + description do frontmatter). */
function SkillList({
  skills,
  selected,
  onOpen,
  loading,
}: {
  skills: SkillSummary[];
  selected: string | null;
  onOpen: (skill: SkillSummary) => void;
  loading?: boolean;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filtered = q
    ? skills.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.dir.toLowerCase().includes(q),
      )
    : skills;

  return (
    <aside className="skill-lib-aside" aria-label="Skills">
      <input
        className="skill-lib-search"
        placeholder="Buscar skill…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={loading}
      />
      {loading ? (
        <p className="skill-lib-empty-small">Carregando skills…</p>
      ) : skills.length === 0 ? (
        <p className="skill-lib-empty-small">Nenhuma skill ainda.</p>
      ) : filtered.length === 0 ? (
        <p className="skill-lib-empty-small">Nada encontrado.</p>
      ) : (
        <div className="skill-lib-groups">
          {SKILL_GROUPS.map(({ key, label }) => {
            const items = filtered.filter((s) => s.origin === key);
            if (items.length === 0) return null;
            return (
              <div key={key} className="skill-lib-group">
                <div className="skill-lib-group-label">
                  {label} <span className="skill-lib-group-count">{items.length}</span>
                </div>
                <ul className="skill-lib-list">
                  {items.map((s) => (
                    <li key={s.path}>
                      <button
                        type="button"
                        className={`skill-lib-item ${selected === s.path ? 'is-active' : ''}`}
                        onClick={() => onOpen(s)}
                        title={s.description || s.name}
                      >
                        <span className="skill-lib-item-name">{s.name}</span>
                        {s.description && <span className="skill-lib-item-desc">{s.description}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </aside>
  );
}

// Ordem dos grupos na lista (por origem). Minhas primeiro; padrão do Hermes por último.
const SKILL_GROUPS: { key: SkillSummary['origin']; label: string }[] = [
  { key: 'mine', label: 'Minhas' },
  { key: 'hermes-run', label: 'Geradas pelo Hermes' },
  { key: 'default', label: 'Padrão do Hermes' },
];

// ---- helpers ----

function errMsg(e: unknown): string {
  const api = e as ApiError;
  if (api?.error) return `Erro: ${api.error}${api.status ? ` (${api.status})` : ''}`;
  return e instanceof Error ? e.message : 'Erro inesperado';
}

function slugifySkill(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function skillTemplate(name: string): string {
  return [
    '---',
    `name: ${name}`,
    'description: TODO — descreva o que a skill faz e QUANDO usá-la (palavras-chave ajudam o agente a escolher).',
    'metadata:',
    '  origin: neo',
    '---',
    '',
    `# ${name}`,
    '',
    '> Generalista, reutilizável entre projetos. Nutre-se da memória do projeto que a importa.',
    '',
    '## O que faz',
    '(uma frase do que esta skill produz)',
    '',
    '## Lê (entrada)',
    '- (artefatos/regras do projeto que esta skill consome)',
    '',
    '## Salva (saída)',
    '- `NN-skill.md` (no projeto)',
    '',
    '## Procedimento',
    '1. …',
    '',
    '## Regras de qualidade',
    '- …',
    '',
  ].join('\n');
}

/** Lê um campo simples do frontmatter YAML (ex: model). null se ausente. */
function getFrontmatterField(md: string, key: string): string | null {
  const m = md.match(/^---\r?\n([\s\S]*?)\r?\n---/);
  if (!m) return null;
  const line = m[1]!.split(/\r?\n/).find((l) => new RegExp(`^\\s*${key}\\s*:`).test(l));
  if (!line) return null;
  return line.replace(new RegExp(`^\\s*${key}\\s*:\\s*`), '').replace(/^["']|["']$/g, '').trim() || null;
}

/** Insere/atualiza um campo simples no frontmatter (cria se não houver). Vazio = remove. */
function setFrontmatterField(md: string, key: string, value: string): string {
  const v = value.trim();
  const m = md.match(/^---\r?\n([\s\S]*?)\r?\n---(\r?\n[\s\S]*)?$/);
  if (!m) return v ? `---\n${key}: ${v}\n---\n\n${md}` : md;
  const rest = m[2] ?? '\n';
  const kept = m[1]!.split(/\r?\n/).filter((l) => !new RegExp(`^\\s*${key}\\s*:`).test(l));
  if (v) kept.push(`${key}: ${v}`);
  return `---\n${kept.join('\n')}\n---${rest}`;
}
